import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  SupplierPaymentWithSupplier,
  SupplierPaymentAllocationWithRelations,
  SupplierPaymentFilters,
  CreateSupplierPaymentData,
} from '../types'

const QUERY_KEY = 'supplier_payments'
const ALLOCATIONS_QUERY_KEY = 'supplier_payment_allocations'

/**
 * Hook pour récupérer les règlements fournisseurs d'une résidence
 */
export function useSupplierPayments(filters: SupplierPaymentFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async (): Promise<SupplierPaymentWithSupplier[]> => {
      let query = supabase
        .from('supplier_payments')
        .select(`
          *,
          suppliers(id, code, name)
        `)
        .eq('residence_id', filters.residence_id)
        .order('payment_date', { ascending: false })

      if (filters.fiscal_year_id) {
        query = query.eq('fiscal_year_id', filters.fiscal_year_id)
      }

      if (filters.supplier_id) {
        query = query.eq('supplier_id', filters.supplier_id)
      }

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.payment_mode) {
        query = query.eq('payment_mode', filters.payment_mode)
      }

      const { data, error } = await query
      if (error) throw error
      return data as SupplierPaymentWithSupplier[]
    },
    enabled: !!filters.residence_id,
  })
}

/**
 * Hook pour récupérer le détail d'un règlement avec ses allocations
 */
export function useSupplierPayment(paymentId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', paymentId],
    queryFn: async (): Promise<SupplierPaymentWithSupplier> => {
      const { data, error } = await supabase
        .from('supplier_payments')
        .select(`
          *,
          suppliers(*),
          supplier_payment_allocations(
            *,
            supplier_invoices(id, invoice_number, internal_reference, total_ttc)
          )
        `)
        .eq('id', paymentId!)
        .single()

      if (error) throw error
      return data as SupplierPaymentWithSupplier
    },
    enabled: !!paymentId,
  })
}

/**
 * Hook pour créer un règlement fournisseur avec ses allocations
 * Utilise la fonction SQL create_supplier_payment qui gère tout
 */
export function useCreateSupplierPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      residenceId,
      fiscalYearId,
      supplierId,
      paymentDate,
      paymentMode,
      bankAccountId,
      allocations,
      reference,
    }: CreateSupplierPaymentData) => {
      const { data, error } = await supabase.rpc('create_supplier_payment', {
        p_residence_id: residenceId,
        p_fiscal_year_id: fiscalYearId,
        p_supplier_id: supplierId,
        p_payment_date: paymentDate,
        p_payment_mode: paymentMode,
        p_bank_account_id: bankAccountId,
        p_invoice_allocations: allocations,
        p_reference: reference || undefined,
      })

      if (error) throw error
      return data as string // ID du règlement créé
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['supplier_invoices'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

/**
 * Hook pour annuler un règlement en attente
 */
export function useCancelSupplierPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const { data, error } = await supabase
        .from('supplier_payments')
        .update({ status: 'CANCELLED' })
        .eq('id', paymentId)
        .eq('status', 'PENDING')
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['supplier_invoices'] })
    },
  })
}

/**
 * Hook pour récupérer les allocations d'un règlement
 */
export function useSupplierPaymentAllocations(paymentId?: string) {
  return useQuery({
    queryKey: [ALLOCATIONS_QUERY_KEY, paymentId],
    queryFn: async (): Promise<SupplierPaymentAllocationWithRelations[]> => {
      const { data, error } = await supabase
        .from('supplier_payment_allocations')
        .select(`
          *,
          supplier_invoices(id, invoice_number, internal_reference, total_ttc)
        `)
        .eq('payment_id', paymentId!)

      if (error) throw error
      return data as SupplierPaymentAllocationWithRelations[]
    },
    enabled: !!paymentId,
  })
}

interface PaymentStats {
  total: number
  executed: number
  pending: number
  count: number
}

/**
 * Hook pour récupérer les statistiques de règlements
 */
export function useSupplierPaymentStats(residenceId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', residenceId, fiscalYearId],
    queryFn: async (): Promise<PaymentStats> => {
      let query = supabase
        .from('supplier_payments')
        .select('amount, status')
        .eq('residence_id', residenceId!)

      if (fiscalYearId) {
        query = query.eq('fiscal_year_id', fiscalYearId)
      }

      const { data, error } = await query
      if (error) throw error

      const payments = data ?? []
      const total = payments.reduce((sum, p) => sum + (p.status !== 'CANCELLED' ? Number(p.amount) : 0), 0)
      const executed = payments.reduce((sum, p) => sum + (p.status === 'EXECUTED' ? Number(p.amount) : 0), 0)
      const pending = payments.reduce((sum, p) => sum + (p.status === 'PENDING' ? Number(p.amount) : 0), 0)
      const count = payments.filter(p => p.status !== 'CANCELLED').length

      return {
        total,
        executed,
        pending,
        count,
      }
    },
    enabled: !!residenceId,
  })
}

/**
 * Hook pour récupérer le solde fournisseur (factures impayées)
 */
export function useSupplierBalance(residenceId?: string, supplierId?: string) {
  return useQuery({
    queryKey: ['supplier_balance', residenceId, supplierId],
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from('supplier_invoices')
        .select('amount_remaining')
        .eq('residence_id', residenceId!)
        .eq('supplier_id', supplierId!)
        .eq('status', 'VALIDATED')
        .neq('payment_status', 'PAID')

      if (error) throw error

      const balance = (data ?? []).reduce((sum, inv) => sum + Number(inv.amount_remaining), 0)
      return balance
    },
    enabled: !!residenceId && !!supplierId,
  })
}
